import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Lock, Mail, User, Briefcase, Trophy } from "lucide-react";
import { Invitation } from "@/services/invitationService";

// Position options for display
const POSITION_OPTIONS = [
  { value: "praktikant", label: "Praktikant" },
  { value: "dual-student", label: "Dualer Student" },
  { value: "beraterassistent", label: "Beraterassistent" },
  { value: "trainee", label: "Trainee" },
  { value: "berater", label: "Berater" }
];

// Password validation function (same as in UserManagement)
const validatePassword = (password: string): { isValid: boolean; message?: string } => {
  if (password.length < 8) {
    return { isValid: false, message: "Das Passwort muss mindestens 8 Zeichen lang sein." };
  }
  
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, message: "Das Passwort muss mindestens einen Großbuchstaben enthalten." };
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return { isValid: false, message: "Das Passwort muss mindestens ein Sonderzeichen enthalten." };
  }
  
  return { isValid: true };
};

interface InvitationActivationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invitation: Invitation | null;
  inviterName: string;
  onActivate: (invitation: Invitation, password: string) => Promise<void>;
  isActivating: boolean;
}

export function InvitationActivationDialog({
  open,
  onOpenChange,
  invitation,
  inviterName,
  onActivate,
  isActivating
}: InvitationActivationDialogProps) {
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (passwordError) {
      setPasswordError(null);
    }
  };

  const handleActivate = async () => {
    if (!invitation) return;

    // Validate password
    const validation = validatePassword(password);
    if (!validation.isValid) {
      setPasswordError(validation.message || "Ungültiges Passwort");
      return;
    }

    try {
      await onActivate(invitation, password);
      // Reset form on success
      setPassword("");
      setPasswordError(null);
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleCancel = () => {
    setPassword("");
    setPasswordError(null);
    onOpenChange(false);
  };

  const getPositionLabel = (position: string) => {
    return POSITION_OPTIONS.find(p => p.value === position)?.label || position;
  };

  if (!invitation) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={20} />
            Einladung aktivieren
          </DialogTitle>
          <DialogDescription>
            Geben Sie ein Passwort für den neuen Benutzer ein. Nach der Aktivierung werden automatisch Punkte vergeben.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Invitation Details */}
          <Card>
            <CardContent className="pt-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <User size={16} className="text-muted-foreground" />
                  <span className="font-medium">{invitation.inviteeName}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-muted-foreground" />
                  <span className="text-sm">{invitation.inviteeEmail}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Briefcase size={16} className="text-muted-foreground" />
                  <span className="text-sm">{getPositionLabel(invitation.inviteePosition)}</span>
                </div>
                
                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Eingeladen von:</span>
                    <span className="text-sm font-medium">{inviterName}</span>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Trophy size={14} className="text-yellow-500" />
                    <span className="text-sm font-semibold text-yellow-600">
                      {invitation.pointsAmount} Punkte
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Password Input */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Lock size={16} />
                Passwort für neuen Benutzer
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                placeholder="Passwort eingeben"
                disabled={isActivating}
              />
              <p className="text-xs text-muted-foreground">
                Das Passwort muss mindestens 8 Zeichen lang sein und mindestens einen Großbuchstaben sowie ein Sonderzeichen enthalten.
              </p>
              {passwordError && (
                <p className="text-xs text-destructive">{passwordError}</p>
              )}
            </div>

            {/* Points Preview */}
            <Card className="bg-muted/50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-yellow-500" />
                    <span className="font-medium">Punkte werden vergeben:</span>
                  </div>
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                    +{invitation.pointsAmount} Punkte an {inviterName}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Nach erfolgreicher Aktivierung werden die Punkte automatisch dem Einladenden gutgeschrieben.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isActivating}
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleActivate} 
            disabled={isActivating || !password}
          >
            {isActivating ? "Wird aktiviert..." : "Benutzer aktivieren"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}